/**
 * Format a notification `raisedAt` timestamp as a human-friendly relative time.
 *
 * Bucketing per §3.9 of `portal-notifications-definition.md`:
 *   < 10 seconds                → "Just now"
 *   10–59 seconds               → "{n} seconds ago"
 *   1–59 minutes                → "{n} min ago"
 *   Same calendar day, ≥ 1 hour → "{n}h ago"
 *   Yesterday                   → "Yesterday"
 *   2–6 calendar days ago       → weekday name (e.g. "Sunday")
 *   ≥ 1 week ago                → absolute short date:
 *                                   same year      → "Apr 6"
 *                                   different year → "Apr 6, 2025"
 *
 * The "same calendar day" / "yesterday" / "weekday" buckets are based on the
 * operator's local calendar (not elapsed milliseconds), so a notification from
 * 23:30 still reads as "Yesterday" at 00:30 rather than flipping between
 * hour-ago and day-ago labels around midnight.
 *
 * @param {string|number|Date|null|undefined} raisedAt
 * @param {Date} [now] Reference point for testability.
 * @returns {string}
 */
export const formatRelativeTime = (raisedAt, now = new Date()) => {
    if (!raisedAt) return '';

    const then = raisedAt instanceof Date ? raisedAt : new Date(raisedAt);
    const thenMs = then.getTime();
    if (Number.isNaN(thenMs)) return '';

    const diffMs = now.getTime() - thenMs;

    // Clock skew / future timestamp → "Just now".
    if (diffMs < 10 * 1000) return 'Just now';

    if (diffMs < 60 * 1000) {
        return `${Math.floor(diffMs / 1000)} seconds ago`;
    }

    const minutes = Math.floor(diffMs / (60 * 1000));
    if (minutes < 60) return `${minutes} min ago`;

    // Calendar-day diff (local time), independent of the minute bucket above.
    const startOfThen = new Date(then.getFullYear(), then.getMonth(), then.getDate()).getTime();
    const startOfNow = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const dayDiff = Math.round((startOfNow - startOfThen) / (24 * 60 * 60 * 1000));

    if (dayDiff <= 0) {
        // Same calendar day → hours.
        const hours = Math.floor(minutes / 60);
        return `${hours}h ago`;
    }
    if (dayDiff === 1) return 'Yesterday';
    if (dayDiff < 7) {
        try {
            return then.toLocaleDateString(undefined, { weekday: 'long' });
        } catch {
            return `${dayDiff} days ago`;
        }
    }

    const sameYear = then.getFullYear() === now.getFullYear();
    try {
        return then.toLocaleDateString(
            undefined,
            sameYear
                ? { month: 'short', day: 'numeric' }
                : { month: 'short', day: 'numeric', year: 'numeric' },
        );
    } catch {
        return then.toISOString().slice(0, 10);
    }
};

/**
 * Format an absolute UTC timestamp for the detail page metadata section.
 *
 * @param {string|number|Date|null|undefined} iso
 * @returns {string}
 */
export const formatAbsoluteTime = (iso) => {
    if (!iso) return '';
    const d = iso instanceof Date ? iso : new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    try {
        return d.toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZoneName: 'short',
        });
    } catch {
        return d.toISOString();
    }
};

/**
 * Compact duration for an alarm that has returned to normal. Shown on the
 * notification list row beside the timer icon. Returns '' when either input
 * is missing or invalid.
 *
 *   < 60 s   → "{n}s"
 *   < 60 min → "{n}m"
 *   < 24 h   → "{h}h {m}m"
 *   ≥ 24 h   → "{d}d {h}h"
 *
 * @param {string|number|Date|null|undefined} raisedAt
 * @param {string|number|Date|null|undefined} returnedToNormalAt
 * @returns {string}
 */
export const formatAlarmDuration = (raisedAt, returnedToNormalAt) => {
    if (!raisedAt || !returnedToNormalAt) return '';
    const start = new Date(raisedAt).getTime();
    const end = new Date(returnedToNormalAt).getTime();
    if (Number.isNaN(start) || Number.isNaN(end)) return '';

    const diff = Math.max(0, end - start);
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ${minutes % 60}m`;
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
};
